import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Cigarette, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PreviewRibbon } from "@/components/cef/cefShared";

/* Rolling Tray v2 — /cef/roll/:serverId — a REAL rolling simulation, not a dot game.
   The joint is drawn live on canvas and physically forms under your pointer:
   1. PAPER — drag a skin out of the pack onto the tray
   2. GRIND — drag across the buds to break them up; crumb size uniformity is scored
   3. SPREAD — drag the herb into the paper trough; column evenness is scored
   4. ROLL — stroke the paper like your fingertips would; smooth, rhythmic strokes
      pack it tight, jerky ones leave it loose and lumpy. Over-rolling chokes it.
   5. SEAL — drag along the gum line to lick it shut
   6. TWIST — circle the tip to close it
   The finished joint renders with YOUR girth, lumpiness, pack and twist — and the
   quality score the server prices comes straight from how it actually looks. */

const STEPS = [
  ["paper", "Grab a skin", "drag a paper out of the pack"],
  ["grind", "Break it up", "drag across the buds — even crumbs burn clean"],
  ["spread", "Fill the trough", "drag herb into the paper, end to end, even"],
  ["roll", "Roll it up", "smooth strokes left-right like fingertips — pack it, don't choke it"],
  ["seal", "Lick the gum", "drag along the gum line, one clean pass"],
  ["twist", "Twist the tip", "circle the end shut"],
];
const COLS = 64;
const TRAY = { paperX: 0.18, paperY: 0.34, paperW: 0.64, paperH: 0.26 };

export default function RollCefPage() {
  const { serverId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const demo = !token;
  const itemKey = params.get("item") || "product|og_kush|mid";
  const kind = params.get("kind") || "joint";

  const canvasRef = useRef(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState(STEPS[0][2]);

  const S = useRef(null); // the whole sim lives here — no re-renders per frame
  if (!S.current) {
    S.current = {
      step: "paper",
      paperPlaced: false,
      draggingPaper: false,
      buds: [{ x: 0.68, y: 0.16, r: 0.045 }, { x: 0.78, y: 0.22, r: 0.038 }, { x: 0.72, y: 0.26, r: 0.03 }],
      particles: [],
      crumbSizes: [],
      herb: new Array(COLS).fill(0),
      pile: 1.0,                 // herb left in the loose pile
      density: 0,                // pack tightness 0..100
      roughness: 0,              // jerky-stroke penalty
      wrap: 0,                   // paper wrap progress 0..1
      strokes: [],
      glue: new Array(COLS).fill(0),
      twist: 0,
      lastA: null,
      pointer: { x: 0, y: 0, down: false },
    };
  }
  const step = STEPS[stepIdx][0];
  const sim = S.current;
  sim.step = step;

  const advance = useCallback((msg) => {
    if (msg) toast.success(msg);
    setStepIdx((i) => { const n = Math.min(i + 1, STEPS.length - 1); setHint(STEPS[n][2]); return n; });
  }, []);

  /* ---------------- pointer interactions ---------------- */
  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  };

  const onDown = (e) => {
    const p = pos(e);
    sim.pointer = { ...p, down: true };
    if (step === "paper") {
      // pack sits top-left; grab a skin by dragging out of it
      if (p.x < 0.16 && p.y < 0.24) sim.draggingPaper = true;
    } else if (step === "twist") {
      sim.lastA = Math.atan2(p.y - TRAY.paperY, p.x - (TRAY.paperX + TRAY.paperW));
    }
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onMove = (e) => {
    const p = pos(e);
    const prev = { ...sim.pointer };
    sim.pointer = { ...p, down: sim.pointer.down };

    if (step === "paper" && sim.draggingPaper && !sim.paperPlaced) {
      if (p.x > TRAY.paperX - 0.05 && p.x < TRAY.paperX + TRAY.paperW && p.y > TRAY.paperY - 0.08 && p.y < TRAY.paperY + TRAY.paperH + 0.1) {
        sim.paperPlaced = true; sim.draggingPaper = false;
        advance("Skin's down — nice and flat");
      }
      return;
    }

    if (step === "grind" && sim.pointer.down) {
      // dragging through a bud chips crumbs off it
      for (const bud of sim.buds) {
        if (bud.r <= 0.004) continue;
        const d = Math.hypot(p.x - bud.x, p.y - bud.y);
        if (d < bud.r + 0.02) {
          const speed = Math.hypot(p.x - prev.x, p.y - prev.y);
          const crumb = Math.min(0.012, 0.004 + speed * 0.12); // fast swipes tear big chunks
          bud.r = Math.max(0, bud.r - crumb * 0.35);
          sim.crumbSizes.push(crumb);
          for (let i = 0; i < 3; i++) sim.particles.push({ x: bud.x + (Math.random() - 0.5) * bud.r, y: bud.y + (Math.random() - 0.5) * bud.r, vx: (Math.random() - 0.5) * 0.01, vy: 0.01 + Math.random() * 0.015, r: crumb });
        }
      }
      return;
    }

    if (step === "spread" && sim.pointer.down && sim.pile > 0.01) {
      // dragging the loose pile over the trough drops herb into the column under your finger
      const inTrough = p.x > TRAY.paperX && p.x < TRAY.paperX + TRAY.paperW && Math.abs(p.y - (TRAY.paperY + TRAY.paperH * 0.5)) < 0.14;
      if (inTrough) {
        const col = Math.min(COLS - 1, Math.max(0, Math.floor(((p.x - TRAY.paperX) / TRAY.paperW) * COLS)));
        const drop = Math.min(sim.pile, 0.012);
        sim.herb[col] += drop;
        if (sim.herb[col + 1] != null) sim.herb[col + 1] += drop * 0.5;
        if (sim.herb[col - 1] != null) sim.herb[col - 1] += drop * 0.5;
        sim.pile -= drop;
      }
      return;
    }

    if (step === "roll" && sim.pointer.down) {
      const onPaper = p.x > TRAY.paperX && p.x < TRAY.paperX + TRAY.paperW && Math.abs(p.y - (TRAY.paperY + TRAY.paperH * 0.5)) < 0.16;
      if (!onPaper) { sim.strokes.push(0); return; }
      const dx = p.x - prev.x;
      const speed = Math.abs(dx) * 1000;
      const dir = Math.sign(dx);
      const last = sim.strokes[sim.strokes.length - 1];
      // a good stroke: steady direction, fingertip speed — like real rolling
      if (speed > 3 && speed < 40 && (last == null || Math.sign(last) === dir || Math.abs(last) < 0.001)) {
        sim.density = Math.min(100, sim.density + 0.22);
      } else {
        sim.density = Math.min(100, sim.density + 0.05);
        sim.roughness += 0.5;
      }
      sim.strokes.push(dx);
      if (sim.strokes.length > 240) sim.strokes.shift();
      sim.wrap = Math.min(1, sim.density / 88);
      // over-rolling past the sweet spot starts to choke it
      if (sim.density > 92) sim.roughness += 0.8;
      return;
    }

    if (step === "seal" && sim.pointer.down && sim.wrap >= 0.8) {
      const gumY = TRAY.paperY + TRAY.paperH * 0.16;
      if (p.x > TRAY.paperX && p.x < TRAY.paperX + TRAY.paperW && Math.abs(p.y - gumY) < 0.05) {
        const col = Math.min(COLS - 1, Math.max(0, Math.floor(((p.x - TRAY.paperX) / TRAY.paperW) * COLS)));
        sim.glue[col] = 1;
      }
      return;
    }

    if (step === "twist" && sim.pointer.down) {
      const cx = TRAY.paperX + TRAY.paperW, cy = TRAY.paperY + TRAY.paperH * 0.4;
      const a = Math.atan2(p.y - cy, p.x - cx);
      if (sim.lastA != null) {
        let da = a - sim.lastA;
        if (da > Math.PI) da -= 2 * Math.PI;
        if (da < -Math.PI) da += 2 * Math.PI;
        sim.twist = Math.min(1, sim.twist + Math.abs(da) / (Math.PI * 3));
      }
      sim.lastA = a;
    }
  };

  const onUp = () => {
    sim.pointer.down = false;
    sim.draggingPaper = false;
    if (step === "grind") {
      const done = sim.buds.every((b) => b.r <= 0.006);
      if (done) {
        // sweep crumbs into a loose pile at the tray base
        sim.particles = [];
        advance("Broken up — smells loud");
      }
    } else if (step === "spread" && sim.pile <= 0.01) {
      advance("Even line — good eye");
    } else if (step === "roll" && sim.wrap >= 1) {
      advance(sim.density > 92 ? "Tight… maybe TOO tight" : sim.density < 55 ? "Bit loose in the middle" : "That's a clean pack");
    } else if (step === "seal") {
      const cover = sim.glue.reduce((a, b) => a + b, 0) / COLS;
      if (cover >= 0.9) advance("Sealed shut");
    } else if (step === "twist" && sim.twist >= 1) {
      finish();
    }
  };

  /* ---------------- scoring + submit ---------------- */
  const computeScores = () => {
    const mean = sim.crumbSizes.reduce((a, b) => a + b, 0) / Math.max(1, sim.crumbSizes.length);
    const variance = sim.crumbSizes.reduce((a, b) => a + (b - mean) ** 2, 0) / Math.max(1, sim.crumbSizes.length);
    const grind = Math.round(Math.max(10, 100 - Math.sqrt(variance) * 9000));
    const herbMean = sim.herb.reduce((a, b) => a + b, 0) / COLS;
    const herbVar = sim.herb.reduce((a, b) => a + (b - herbMean) ** 2, 0) / COLS;
    const spread = Math.round(Math.max(5, 100 - Math.sqrt(herbVar) * 900));
    const densTarget = Math.max(0, 100 - Math.abs(76 - sim.density) * 2.2); // 76 = the sweet pack
    const smooth = Math.max(0, 100 - sim.roughness * 2.4);
    const glueCover = sim.glue.reduce((a, b) => a + b, 0) / COLS;
    const sealTwist = glueCover * 0.6 + sim.twist * 0.4;
    // map onto the server's scoring contract (timing targets 900ms / 1800ms)
    const timing_ms = Math.round(900 + (100 - (densTarget * 0.7 + smooth * 0.3)) * 18);
    const seal_hold_ms = Math.round(sealTwist * 1800 + 100);
    return { grind_score: Math.min(100, grind), spread_score: Math.min(100, spread), timing_ms, seal_hold_ms };
  };

  const describeJoint = (q) => {
    const fat = sim.herb.reduce((a, b) => a + b, 0) / COLS > 0.45;
    if (q >= 85) return `${fat ? "Fat, tight, textbook" : "Tight and true"} — this one goes in the top jar.`;
    if (sim.density > 92) return "Packed like a baton — it won't pull. Choker.";
    if (sim.density < 55) return "Loose as a handshake from a cop — it'll run and canoe.";
    if (q >= 60) return "Decent everyday roll — a little lumpy but she burns.";
    return "Pregnant in the middle, loose at the ends. The boys will roast you for it.";
  };

  const finish = async () => {
    setBusy(true);
    const payload = { item_key: itemKey, kind, ...computeScores() };
    if (demo) {
      const q = Math.round(payload.grind_score * 0.2 + payload.spread_score * 0.2 + Math.max(0, 100 - Math.abs(900 - payload.timing_ms) / 9) * 0.35 + Math.max(0, 100 - Math.abs(1800 - payload.seal_hold_ms) / 18) * 0.25);
      setResult({ quality: q, message: describeJoint(q) + " (preview)", price_mult: 1.0 });
      sim.step = "done"; setBusy(false); return;
    }
    try {
      const r = await api.post(`/nc/servers/${serverId}/grow/roll`, payload, { params: { token } });
      setResult({ ...r.data, message: describeJoint(r.data.quality) });
      sim.step = "done";
    } catch (e) { toast.error(e.response?.data?.detail || "Rolling failed"); }
    finally { setBusy(false); }
  };

  const reset = () => {
    S.current = null; setResult(null); setStepIdx(0); setHint(STEPS[0][2]); setBusy(false);
    // re-init sim
    S.current = { step: "paper", paperPlaced: false, draggingPaper: false,
      buds: [{ x: 0.68, y: 0.16, r: 0.045 }, { x: 0.78, y: 0.22, r: 0.038 }, { x: 0.72, y: 0.26, r: 0.03 }],
      particles: [], crumbSizes: [], herb: new Array(COLS).fill(0), pile: 1.0, density: 0, roughness: 0,
      wrap: 0, strokes: [], glue: new Array(COLS).fill(0), twist: 0, lastA: null, pointer: { x: 0, y: 0, down: false } };
  };

  /* ---------------- canvas renderer ---------------- */
  useEffect(() => {
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let W, H;
    const fit = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = canvas.clientWidth; H = canvas.clientHeight;
      canvas.width = W * dpr; canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    fit();
    window.addEventListener("resize", fit);

    const herbColor = (a = 1) => `rgba(86,130,62,${a})`;

    const frame = (now) => {
      const t = now / 1000;
      ctx.clearRect(0, 0, W, H);

      // wooden tray
      const wood = ctx.createLinearGradient(0, 0, 0, H);
      wood.addColorStop(0, "#241a10"); wood.addColorStop(0.5, "#2e2115"); wood.addColorStop(1, "#1c130c");
      ctx.fillStyle = wood;
      ctx.beginPath(); ctx.roundRect(W * 0.04, H * 0.06, W * 0.92, H * 0.88, 18); ctx.fill();
      ctx.strokeStyle = "rgba(255,220,160,0.12)"; ctx.lineWidth = 2; ctx.stroke();
      // wood grain
      ctx.strokeStyle = "rgba(0,0,0,0.15)"; ctx.lineWidth = 1;
      for (let i = 0; i < 9; i++) {
        ctx.beginPath();
        ctx.moveTo(W * 0.06, H * (0.12 + i * 0.09));
        ctx.bezierCurveTo(W * 0.35, H * (0.1 + i * 0.095 + Math.sin(i) * 0.01), W * 0.6, H * (0.14 + i * 0.09), W * 0.94, H * (0.11 + i * 0.09));
        ctx.stroke();
      }

      const px = TRAY.paperX * W, py = TRAY.paperY * H, pw = TRAY.paperW * W, ph = TRAY.paperH * H;

      // paper pack (top-left) — before placement
      if (!sim.paperPlaced && sim.step === "paper") {
        ctx.fillStyle = "#173a5e";
        ctx.beginPath(); ctx.roundRect(W * 0.03, H * 0.04, W * 0.11, H * 0.17, 4); ctx.fill();
        ctx.fillStyle = "rgba(255,255,255,0.85)";
        ctx.font = `700 ${H * 0.022}px monospace`;
        ctx.fillText("RIZLA", W * 0.048, H * 0.13);
        ctx.fillStyle = "rgba(255,255,255,0.5)";
        ctx.font = `${H * 0.016}px monospace`;
        ctx.fillText("king size", W * 0.048, H * 0.16);
        // a skin peeking out — the drag target
        const bob = Math.sin(t * 2) * 2;
        ctx.fillStyle = "rgba(245,240,225,0.95)";
        ctx.beginPath(); ctx.roundRect(W * 0.085, H * 0.05 - bob, W * 0.075, H * 0.11, 2); ctx.fill();
        ctx.strokeStyle = "rgba(150,120,60,0.6)";
        ctx.strokeRect(W * 0.085, H * 0.05 - bob, W * 0.075, H * 0.11);
        if (sim.draggingPaper) {
          ctx.fillStyle = "rgba(245,240,225,0.95)";
          ctx.beginPath(); ctx.roundRect(sim.pointer.x * W - pw / 2, sim.pointer.y * H - ph / 2, pw, ph, 3); ctx.fill();
        }
      }

      // buds — shrink as you grind
      for (const bud of sim.buds) {
        if (bud.r <= 0.004) continue;
        const bx = bud.x * W, by = bud.y * H, br = bud.r * W;
        const g = ctx.createRadialGradient(bx - br * 0.3, by - br * 0.3, br * 0.1, bx, by, br);
        g.addColorStop(0, "#7ba05b"); g.addColorStop(0.7, "#4a6b35"); g.addColorStop(1, "#33491f");
        ctx.fillStyle = g;
        ctx.beginPath(); ctx.arc(bx, by, br, 0, 6.2832); ctx.fill();
        // trichome sparkle
        ctx.fillStyle = "rgba(230,240,210,0.5)";
        for (let i = 0; i < 6; i++) ctx.fillRect(bx + Math.sin(i * 2.4) * br * 0.7, by + Math.cos(i * 1.9) * br * 0.7, 2, 2);
      }

      // flying crumbs
      for (const p of sim.particles) {
        p.x += p.vx; p.y += p.vy; p.vy += 0.0012;
        if (p.y > 0.88) { p.y = 0.88; p.vy = 0; p.vx = 0; }
        ctx.fillStyle = herbColor(0.9);
        ctx.beginPath(); ctx.arc(p.x * W, p.y * H, Math.max(1.5, p.r * W * 0.5), 0, 6.2832); ctx.fill();
      }

      // loose herb pile (grind → spread handoff)
      if ((sim.step === "spread" || sim.step === "grind") && sim.buds.every((b) => b.r <= 0.006)) {
        const pileW = 0.16 * sim.pile + 0.02;
        ctx.fillStyle = herbColor(0.95);
        ctx.beginPath(); ctx.ellipse(W * 0.82, H * 0.86, pileW * W, pileW * H * 0.55, 0, 0, 6.2832); ctx.fill();
        ctx.fillStyle = "rgba(60,40,20,0.4)";
        for (let i = 0; i < 30 * sim.pile; i++) ctx.fillRect(W * (0.82 + (Math.sin(i * 12.9) * pileW * 0.8)), H * (0.86 + Math.cos(i * 7.7) * pileW * 0.4), 2, 1);
      }

      // ── the joint itself ──
      if (sim.paperPlaced || sim.step !== "paper") {
        const densF = Math.min(1, sim.density / 100);
        // paper sheet / forming tube
        const paperGrad = ctx.createLinearGradient(0, py, 0, py + ph);
        paperGrad.addColorStop(0, "rgba(246,242,230,0.96)");
        paperGrad.addColorStop(0.75, "rgba(228,222,205,0.96)");
        paperGrad.addColorStop(1, "rgba(205,198,180,0.96)");
        ctx.fillStyle = paperGrad;
        ctx.beginPath(); ctx.roundRect(px, py, pw, ph, [3, 3, 10, 10]); ctx.fill();
        // crease shadow (the trough)
        ctx.fillStyle = "rgba(120,100,70,0.25)";
        ctx.fillRect(px, py + ph * 0.46, pw, ph * 0.08);

        // herb bed inside the trough — compresses as you roll
        const colW = pw / COLS;
        for (let i = 0; i < COLS; i++) {
          const h0 = Math.min(1, sim.herb[i] * 1.9);
          if (h0 <= 0) continue;
          const compact = 1 - densF * 0.45;
          const hh = h0 * ph * 0.42 * compact;
          const cx = px + i * colW;
          ctx.fillStyle = herbColor(0.95);
          ctx.fillRect(cx, py + ph * 0.5 - hh, colW + 0.5, hh);
          // flecks
          ctx.fillStyle = "rgba(50,60,30,0.7)";
          ctx.fillRect(cx + colW * 0.3, py + ph * 0.5 - hh * 0.6, 1.5, 1.5);
        }

        // the wrap — front lip curls over with density
        if (sim.wrap > 0) {
          const lipH = ph * (0.3 + sim.wrap * 0.62);
          const lipGrad = ctx.createLinearGradient(0, py + ph - lipH, 0, py + ph);
          lipGrad.addColorStop(0, "rgba(250,246,235,0.98)");
          lipGrad.addColorStop(1, "rgba(215,208,190,0.98)");
          ctx.fillStyle = lipGrad;
          ctx.beginPath();
          ctx.moveTo(px, py + ph);
          ctx.lineTo(px, py + ph - lipH * (0.4 + sim.wrap * 0.6));
          // per-column lumpiness rides the lip — a bad spread shows through the paper
          for (let i = 0; i <= COLS; i += 4) {
            const lump = (sim.herb[Math.min(COLS - 1, i)] || 0) * ph * 0.18 * (1 - densF * 0.5);
            ctx.lineTo(px + (i / COLS) * pw, py + ph - lipH * (0.4 + sim.wrap * 0.6) - lump);
          }
          ctx.lineTo(px + pw, py + ph);
          ctx.closePath(); ctx.fill();
        }

        // gum line (seal step) — shimmer strip along the top
        if (sim.wrap >= 0.8) {
          const gumY = py + ph * 0.16;
          for (let i = 0; i < COLS; i++) {
            const wet = sim.glue[i];
            ctx.fillStyle = wet ? "rgba(180,220,255,0.75)" : `rgba(200,180,140,${0.25 + 0.1 * Math.sin(t * 3 + i * 0.4)})`;
            ctx.fillRect(px + i * colW, gumY, colW + 0.5, ph * 0.045);
          }
        }

        // sealed → it's a tube: crutch left, twist right
        if (sim.step === "twist" || sim.step === "done") {
          // crutch
          ctx.fillStyle = "#c9a86a";
          ctx.beginPath(); ctx.roundRect(px - pw * 0.06, py + ph * 0.28, pw * 0.07, ph * 0.5, 3); ctx.fill();
          ctx.strokeStyle = "rgba(90,70,40,0.5)";
          for (let i = 0; i < 4; i++) { ctx.beginPath(); ctx.moveTo(px - pw * 0.06, py + ph * (0.32 + i * 0.11)); ctx.lineTo(px + pw * 0.01, py + ph * (0.32 + i * 0.11)); ctx.stroke(); }
          // twisted tip
          const tipX = px + pw, tipY = py + ph * 0.42;
          const twistPx = sim.twist * pw * 0.075;
          ctx.fillStyle = "rgba(238,232,215,0.97)";
          ctx.beginPath();
          ctx.moveTo(tipX, py + ph * 0.18);
          ctx.lineTo(tipX + twistPx + 4, tipY);
          ctx.lineTo(tipX, py + ph * 0.66);
          ctx.closePath(); ctx.fill();
          ctx.strokeStyle = "rgba(140,120,90,0.55)";
          for (let i = 1; i <= 3; i++) {
            ctx.beginPath();
            ctx.moveTo(tipX + (twistPx + 4) * (i / 4), py + ph * (0.18 + 0.24 * (i / 4)));
            ctx.lineTo(tipX + (twistPx + 4) * (i / 4), py + ph * (0.66 - 0.24 * (i / 4)));
            ctx.stroke();
          }
        }

        // pack meter — subtle, bottom of tray
        if (sim.step === "roll") {
          const mx = W * 0.3, my = H * 0.86, mw = W * 0.4;
          ctx.fillStyle = "rgba(0,0,0,0.5)"; ctx.fillRect(mx, my, mw, 8);
          const sweet = sim.density > 60 && sim.density < 92;
          ctx.fillStyle = sim.density > 92 ? "#ef4444" : sweet ? "#4ade80" : "#f59e0b";
          ctx.fillRect(mx, my, (sim.density / 100) * mw, 8);
          ctx.strokeStyle = "rgba(255,255,255,0.25)"; ctx.strokeRect(mx, my, mw, 8);
          ctx.fillStyle = "rgba(255,255,255,0.35)"; ctx.fillRect(mx + mw * 0.6, my - 2, 1, 12); ctx.fillRect(mx + mw * 0.92, my - 2, 1, 12);
        }
      }

      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", fit); document.documentElement.style.background = ""; document.body.style.background = ""; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="roll-page" className="min-h-screen bg-transparent flex flex-col items-center justify-end pb-6 relative overflow-hidden select-none">
      <style>{`@keyframes ncFadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }`}</style>
      {demo && <PreviewRibbon what="rolling tray" />}

      {/* step strip — minimal chrome, the tray is the UI */}
      <div className="absolute top-4 inset-x-0 flex flex-col items-center gap-2 pointer-events-none z-10">
        <div className="flex items-center gap-1.5 bg-black/80 border border-white/15 px-3 py-2 backdrop-blur-md">
          <Cigarette className="w-3.5 h-3.5 text-emerald-400" />
          {STEPS.map(([id, label], i) => (
            <span key={id} data-testid={`roll-step-${id}`}
              className={`font-mono text-[8px] uppercase tracking-[0.15em] px-2 py-1 border ${i < stepIdx ? "border-emerald-500/40 text-emerald-400" : i === stepIdx ? "border-white/40 text-white bg-white/10" : "border-white/10 text-zinc-600"}`}>
              {label}
            </span>
          ))}
        </div>
        <p data-testid="roll-hint" className="font-mono text-[9px] text-zinc-300 bg-black/70 px-3 py-1.5 border border-white/10 uppercase tracking-[0.2em]">{hint}</p>
      </div>

      <canvas data-testid="roll-canvas" ref={canvasRef}
        className="w-full max-w-4xl h-[68vh] cursor-grab active:cursor-grabbing touch-none"
        style={{ animation: "ncFadeUp 0.35s ease both" }}
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp} />

      {/* result overlay */}
      {result && (
        <div data-testid="roll-result" className="absolute inset-0 z-20 flex items-end justify-center pb-10 bg-black/40">
          <div className="w-full max-w-md border border-white/15 bg-black/92 backdrop-blur-md p-6 text-center" style={{ animation: "ncFadeUp 0.3s ease both" }}>
            <p className="font-mono text-[9px] uppercase tracking-[0.35em] text-zinc-500">Off the tray</p>
            <p className="font-mono font-bold text-5xl mt-3" style={{ color: result.quality >= 70 ? "#4ade80" : result.quality >= 45 ? "#f59e0b" : "#ef4444" }}>
              {result.quality}<span className="text-lg text-zinc-500">/100</span>
            </p>
            <p className="font-serif italic text-[13px] text-zinc-200 mt-2 leading-snug">{result.message}</p>
            <p className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.2em] mt-2">quality is pride, not profit — word travels</p>
            <div className="flex gap-2 mt-4">
              <button data-testid="roll-again" onClick={reset} disabled={busy}
                className="flex-1 py-3 font-mono text-[10px] font-bold uppercase tracking-[0.2em] bg-emerald-500 text-black flex items-center justify-center gap-1.5">
                <RotateCcw className="w-3.5 h-3.5" /> Roll another
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
